import type { Paragraph } from '@/api/types/stowed-element'
import { unbakeShapeTextParagraphs } from './helpers/unbake-shape-text-paragraphs'
import { parseParagraph } from './helpers/parse-paragraph'

/**
 * `content.text` key *presence* — not paragraph count — is the "this shape carries text" signal
 * (WMPCM-8160, wire side): `buildShapeTextContent` (stowed-element-builder.ts) always writes the
 * key once `shape.paragraphs != null`, even for an empty array, precisely so an empty converted
 * frame survives a real reload as a text frame rather than reverting to a plain shape. Requiring
 * a non-empty array here would silently undo that guarantee on the read side. `editorShape` is a
 * same-session-only fallback (it never survives the backend round-trip — see
 * docs/text-unification.md §2.3) and keeps its original non-empty threshold, since it is not
 * itself a durable "is this a text frame" signal.
 */
export function readShapeTextParagraphsFromDecoded(
  contentDecoded: Record<string, unknown>,
  _kindId: number
): Paragraph[] | undefined {
  const content = (contentDecoded.content ?? contentDecoded.Content) as
    Record<string, unknown> | undefined
  const text = (content?.text ?? content?.Text) as Record<string, unknown> | undefined

  // Field-by-field validation/coercion (parseParagraph) — a malformed/unexpected shape in
  // decoded JSON is dropped/defaulted here instead of being blindly cast through — then
  // `unbakeShapeTextParagraphs` collapses yb360's baked visual scanlines back to larry's logical
  // one-line-per-paragraph model. Applied here, at the single choke point, rather than in the
  // callers, so it covers BOTH load paths: `checkpointToShapes` (reload) and `decodedDataToShape`
  // (remote edits and this client's own echo, which never involve a reload at all). Near-identity
  // on the `editorShape` branch below, whose copy is already logical.
  const parse = (raw: unknown): Paragraph[] =>
    Array.isArray(raw)
      ? unbakeShapeTextParagraphs(raw.map(parseParagraph).filter((p): p is Paragraph => p !== null))
      : []

  if (text != null) {
    return parse(text.paragraphs ?? text.Paragraphs)
  }

  const editorShape = (contentDecoded.editorShape ?? contentDecoded.EditorShape) as
    Record<string, unknown> | undefined
  const editorParagraphsRaw = editorShape?.paragraphs ?? editorShape?.ShapeTextParagraphs

  if (!Array.isArray(editorParagraphsRaw) || editorParagraphsRaw.length === 0) return undefined

  const paragraphs = parse(editorParagraphsRaw)

  return paragraphs.length > 0 ? paragraphs : undefined
}
